import { useState } from "react";
import { IconChevronLeft, IconChevronRight } from "@/components/icons";
import {
  MONTH_LABELS_RU,
  WEEKDAY_LABELS_RU,
  buildCalendarDays,
  formatIsoDate,
  isoDatePart,
  isIsoDateInRange,
  parseIsoDate,
} from "@/lib/dates/isoDate";

type MonthState = { year: number; month: number };

function monthFromValue(value: string, min?: string, max?: string): MonthState {
  const selected = parseIsoDate(isoDatePart(value));
  if (selected) {
    return { year: selected.getFullYear(), month: selected.getMonth() };
  }

  const today = new Date();
  const todayIso = formatIsoDate(today);
  if (max && todayIso > isoDatePart(max)) {
    const bound = parseIsoDate(isoDatePart(max));
    if (bound) return { year: bound.getFullYear(), month: bound.getMonth() };
  }
  if (min && todayIso < isoDatePart(min)) {
    const bound = parseIsoDate(isoDatePart(min));
    if (bound) return { year: bound.getFullYear(), month: bound.getMonth() };
  }

  return { year: today.getFullYear(), month: today.getMonth() };
}

function yearBounds(min?: string, max?: string): { from: number; to: number } {
  const now = new Date().getFullYear();
  const from = min ? Number(isoDatePart(min).slice(0, 4)) : now - 120;
  const to = max ? Number(isoDatePart(max).slice(0, 4)) : now + 10;
  return {
    from: Number.isFinite(from) ? from : now - 120,
    to: Number.isFinite(to) ? to : now + 10,
  };
}

function shiftMonth(current: MonthState, delta: number): MonthState {
  const date = new Date(current.year, current.month + delta, 1);
  return { year: date.getFullYear(), month: date.getMonth() };
}

export function DateCalendar({
  value,
  min,
  max,
  onSelect,
  onToday,
  onClear,
}: {
  value: string;
  min?: string;
  max?: string;
  onSelect: (iso: string) => void;
  onToday?: () => void;
  onClear?: () => void;
}) {
  const selectedDay = isoDatePart(value);
  const todayIso = formatIsoDate(new Date());
  const [visibleMonth, setVisibleMonth] = useState<MonthState>(() =>
    monthFromValue(value, min, max),
  );
  const years = yearBounds(min, max);
  const days = buildCalendarDays(visibleMonth.year, visibleMonth.month);
  const todayAllowed = isIsoDateInRange(todayIso, min, max);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-1">
        <button
          type="button"
          title="Предыдущий месяц"
          aria-label="Предыдущий месяц"
          onClick={() => setVisibleMonth((current) => shiftMonth(current, -1))}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
        >
          <IconChevronLeft size={16} />
        </button>
        <div className="flex min-w-0 flex-1 items-center justify-center gap-1">
          <select
            aria-label="Месяц"
            value={visibleMonth.month}
            onChange={(event) =>
              setVisibleMonth((current) => ({
                ...current,
                month: Number(event.target.value),
              }))
            }
            className="max-w-[8.5rem] rounded-md border-0 bg-transparent py-0.5 pl-1 pr-0 font-onest text-sm font-semibold capitalize text-ink focus:ring-2 focus:ring-brand-500/20"
          >
            {MONTH_LABELS_RU.map((label, month) => (
              <option key={label} value={month}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="Год"
            value={visibleMonth.year}
            onChange={(event) =>
              setVisibleMonth((current) => ({
                ...current,
                year: Number(event.target.value),
              }))
            }
            className="rounded-md border-0 bg-transparent py-0.5 pl-1 pr-0 font-onest text-sm font-semibold text-ink focus:ring-2 focus:ring-brand-500/20"
          >
            {Array.from(
              { length: years.to - years.from + 1 },
              (_, index) => years.to - index,
            ).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          title="Следующий месяц"
          aria-label="Следующий месяц"
          onClick={() => setVisibleMonth((current) => shiftMonth(current, 1))}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
        >
          <IconChevronRight size={16} />
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-0.5">
        {WEEKDAY_LABELS_RU.map((label) => (
          <div
            key={label}
            className="py-1 text-center font-onest text-[10px] font-medium uppercase tracking-wide text-slate-400"
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day) => {
          const disabled = !isIsoDateInRange(day.iso, min, max);
          const isSelected = day.iso === selectedDay;
          const isToday = day.iso === todayIso;
          return (
            <button
              key={day.iso}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(day.iso)}
              className={[
                "h-8 rounded-lg font-onest text-xs tabular-nums",
                disabled
                  ? "cursor-not-allowed text-slate-300"
                  : day.inMonth
                    ? "text-slate-800"
                    : "text-slate-300",
                isSelected
                  ? "bg-brand-500 text-white"
                  : !disabled && isToday
                    ? "bg-brand-50 font-medium text-brand-700"
                    : disabled
                      ? ""
                      : "hover:bg-slate-100",
              ].join(" ")}
            >
              {day.day}
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
        {onToday && todayAllowed ? (
          <button
            type="button"
            onClick={onToday}
            className="font-onest text-xs font-medium text-brand-700 hover:underline"
          >
            Сегодня
          </button>
        ) : (
          <span />
        )}
        {onClear ? (
          <button
            type="button"
            onClick={onClear}
            className="font-onest text-xs text-slate-500 hover:underline"
          >
            Сбросить
          </button>
        ) : null}
      </div>
    </div>
  );
}
